'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface SearchBarProps {
  onClose?: () => void;
}

interface SearchSuggestion {
  id: string;
  type: 'brand' | 'model' | 'category';
  text: string;
  category?: string;
}

const SearchBar = ({ onClose }: SearchBarProps) => {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const mockSuggestions: SearchSuggestion[] = [
    { id: '1', type: 'brand', text: 'iPhone', category: 'Apple' },
    { id: '2', type: 'model', text: 'iPhone 14 Pro', category: 'Apple' },
    { id: '3', type: 'model', text: 'iPhone 13', category: 'Apple' },
    { id: '4', type: 'brand', text: 'Samsung Galaxy', category: 'Samsung' },
    { id: '5', type: 'model', text: 'Samsung Galaxy S23', category: 'Samsung' },
    { id: '6', type: 'category', text: 'Smartphones' },
    { id: '7', type: 'category', text: 'Oppo' },
    { id: '8', type: 'category', text: 'Huawei' },
  ];

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = mockSuggestions.filter((suggestion) =>
        suggestion.text.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setSelectedIndex(-1);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (query: string) => {
    if (query.trim()) {
      router.push(`/homepage?search=${encodeURIComponent(query)}`);
      setShowSuggestions(false);
      setSearchQuery('');
      onClose?.();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedIndex >= 0 && suggestions[selectedIndex]) {
      handleSearch(suggestions[selectedIndex].text);
    } else {
      handleSearch(searchQuery);
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    handleSearch(suggestion.text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case 'brand':
        return 'BuildingStorefrontIcon';
      case 'model':
        return 'DevicePhoneMobileIcon';
      case 'category':
        return 'TagIcon';
      default:
        return 'MagnifyingGlassIcon';
    }
  };

  return (
    <div data-component-id="src/components/common/SearchBar.tsx:125:4" data-component-path="src/components/common/SearchBar.tsx" data-component-line="125" data-component-end-line="199" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%22%7D" className="relative w-full">
      <form data-component-id="src/components/common/SearchBar.tsx:126:6" data-component-path="src/components/common/SearchBar.tsx" data-component-line="126" data-component-end-line="158" data-component-file="SearchBar.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22relative%22%7D" onSubmit={handleSubmit} className="relative">
        <div data-component-id="src/components/common/SearchBar.tsx:127:8" data-component-path="src/components/common/SearchBar.tsx" data-component-line="127" data-component-end-line="157" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20flex%20items-center%22%7D" className="relative flex items-center">
          <Icon data-component-id="src/components/common/SearchBar.tsx:128:10" data-component-path="src/components/common/SearchBar.tsx" data-component-line="128" data-component-end-line="132" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-4%20text-muted-foreground%22%7D"
            name="MagnifyingGlassIcon"
            size={20}
            className="absolute left-4 text-muted-foreground"
          />
          <input data-component-id="src/components/common/SearchBar.tsx:133:10" data-component-path="src/components/common/SearchBar.tsx" data-component-line="133" data-component-end-line="142" data-component-file="SearchBar.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22h-12%20w-full%20rounded-xl%20border%20border-border%20bg-background%20pl-12%20pr-12%20text-foreground%20placeholder%3Atext-muted-foreground%20transition-smooth%20focus%3Aborder-primary%20focus%3Aoutline-none%20focus%3Aring-3%20focus%3Aring-primary%20focus%3Aring-offset-3%20focus%3Aring-offset-background%22%7D"
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
            placeholder="Search by brand, model, or category..."
            className="h-12 w-full rounded-xl border border-border bg-background pl-12 pr-12 text-foreground placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-3 focus:ring-primary focus:ring-offset-3 focus:ring-offset-background"
          />
          {searchQuery && (
            <button data-component-id="src/components/common/SearchBar.tsx:144:12" data-component-path="src/components/common/SearchBar.tsx" data-component-line="144" data-component-end-line="155" data-component-file="SearchBar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22absolute%20right-4%20flex%20h-6%20w-6%20items-center%20justify-center%20rounded-full%20text-muted-foreground%20transition-smooth%20hover%3Abg-muted%20hover%3Atext-foreground%22%7D"
              type="button"
              onClick={() => {
                setSearchQuery('');
                setShowSuggestions(false);
                inputRef.current?.focus();
              }}
              className="absolute right-4 flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-smooth hover:bg-muted hover:text-foreground"
              aria-label="Clear search"
            >
              <Icon data-component-id="src/components/common/SearchBar.tsx:154:14" data-component-path="src/components/common/SearchBar.tsx" data-component-line="154" data-component-end-line="154" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={16} />
            </button>
          )}
        </div>
      </form>

      {showSuggestions && suggestions.length > 0 && (
        <div data-component-id="src/components/common/SearchBar.tsx:161:8" data-component-path="src/components/common/SearchBar.tsx" data-component-line="161" data-component-end-line="197" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-full%20z-%5B200%5D%20mt-2%20w-full%20overflow-hidden%20rounded-xl%20border%20border-border%20bg-popover%20shadow-elevation-2%22%7D"
          ref={suggestionsRef}
          className="absolute top-full z-[200] mt-2 w-full overflow-hidden rounded-xl border border-border bg-popover shadow-elevation-2"
        >
          <div data-component-id="src/components/common/SearchBar.tsx:165:10" data-component-path="src/components/common/SearchBar.tsx" data-component-line="165" data-component-end-line="196" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-h-80%20overflow-y-auto%22%7D" className="max-h-80 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <button data-component-id="src/components/common/SearchBar.tsx:167:14" data-component-path="src/components/common/SearchBar.tsx" data-component-line="167" data-component-end-line="194" data-component-file="SearchBar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={suggestion.id}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-smooth hover:bg-muted ${
                  index === selectedIndex ? 'bg-muted' : ''
                }`}
              >
                <Icon data-component-id="src/components/common/SearchBar.tsx:174:16" data-component-path="src/components/common/SearchBar.tsx" data-component-line="174" data-component-end-line="178" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-muted-foreground%22%7D"
                  name={getSuggestionIcon(suggestion.type)}
                  size={18}
                  className="text-muted-foreground"
                />
                <div data-component-id="src/components/common/SearchBar.tsx:179:16" data-component-path="src/components/common/SearchBar.tsx" data-component-line="179" data-component-end-line="188" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
                  <div data-component-id="src/components/common/SearchBar.tsx:180:18" data-component-path="src/components/common/SearchBar.tsx" data-component-line="180" data-component-end-line="182" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20font-medium%20text-popover-foreground%22%7D" className="text-sm font-medium text-popover-foreground">
                    {suggestion.text}
                  </div>
                  {suggestion.category && (
                    <div data-component-id="src/components/common/SearchBar.tsx:184:20" data-component-path="src/components/common/SearchBar.tsx" data-component-line="184" data-component-end-line="186" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22caption%20text-muted-foreground%22%7D" className="caption text-muted-foreground">
                      {suggestion.category}
                    </div>
                  )}
                </div>
                <Icon data-component-id="src/components/common/SearchBar.tsx:189:16" data-component-path="src/components/common/SearchBar.tsx" data-component-line="189" data-component-end-line="193" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowUpRightIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D"
                  name="ArrowUpRightIcon"
                  size={16}
                  className="text-muted-foreground"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;